import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  Stack,
  TextField,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Checkroom,
  Build,
  CloudUpload,
  Palette,
  Style,
  TrendingUp,
  Email,
} from "@mui/icons-material";
import emailjs from "@emailjs/browser";
import { EMAILJS_CONFIG } from "../config/emailjs";

interface HomePageProps {
  onLoginClick: () => void;
  onRegisterClick: () => void;
  onDemoClick: () => void;
  isMobile?: boolean;
  isTablet?: boolean;
}

export const HomePage: React.FC<HomePageProps> = ({
  onLoginClick,
  onRegisterClick,
  onDemoClick,
  isMobile = false,
  isTablet = false,
}) => {
  const [feedbackForm, setFeedbackForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    open: boolean;
    success: boolean;
    message: string;
  }>({
    open: false,
    success: false,
    message: "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFeedbackForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Check if EmailJS is properly configured
      const isEmailJSConfigured =
        EMAILJS_CONFIG.SERVICE_ID !== "your_service_id" &&
        EMAILJS_CONFIG.TEMPLATE_ID !== "your_template_id" &&
        EMAILJS_CONFIG.PUBLIC_KEY !== "your_public_key";

      if (isEmailJSConfigured) {
        // Use EmailJS for sending emails
        const templateParams = {
          from_name: feedbackForm.name || "Anonymous",
          from_email: feedbackForm.email || "No email provided",
          subject: `NO STYLIST Feedback: ${feedbackForm.subject}`,
          message: feedbackForm.message,
          to_email: "wardrobestylistai@gmail.com",
          reply_to: feedbackForm.email || "wardrobestylistai@gmail.com",
        };

        await emailjs.send(
          EMAILJS_CONFIG.SERVICE_ID,
          EMAILJS_CONFIG.TEMPLATE_ID,
          templateParams,
          EMAILJS_CONFIG.PUBLIC_KEY
        );
      } else {
        // Fallback: Log feedback to console and simulate success
        // Simulate processing time
        await new Promise<void>((resolve) => setTimeout(resolve, 1000));
      }

      // Show success message
      setSubmitStatus({
        open: true,
        success: true,
        message: "Thank you for your feedback! It has been recorded.",
      });

      // Reset form
      setFeedbackForm({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      // Provide more specific error messages
      let errorMessage = "Failed to submit feedback. Please try again later.";

      if (error instanceof Error) {
        if (error.message.includes("Invalid template")) {
          errorMessage = "Email template error. Please try again.";
        } else if (error.message.includes("Invalid service")) {
          errorMessage = "Email service error. Please try again.";
        }
      }

      setSubmitStatus({
        open: true,
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSubmitStatus((prev) => ({ ...prev, open: false }));
  };

  const features = [
    {
      icon: <Checkroom sx={{ fontSize: 40, color: "#C49A88" }} />,
      title: "Smart Wardrobe",
      description:
        "Organize and categorize your clothing items with AI-powered analysis",
    },
    {
      icon: <Build sx={{ fontSize: 40, color: "#C49A88" }} />,
      title: "Outfit Builder",
      description:
        "Create stylish outfits from your existing wardrobe with color coordination",
    },
    {
      icon: <CloudUpload sx={{ fontSize: 40, color: "#C49A88" }} />,
      title: "Easy Upload",
      description:
        "Simply photograph your clothes and let AI identify colors, patterns, and styles",
    },
    {
      icon: <Palette sx={{ fontSize: 40, color: "#C49A88" }} />,
      title: "Color Analysis",
      description:
        "Advanced color matching technology ensures perfect outfit coordination",
    },
    {
      icon: <Style sx={{ fontSize: 40, color: "#C49A88" }} />,
      title: "Style Insights",
      description:
        "Get personalized recommendations based on your wardrobe and preferences",
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: "#C49A88" }} />,
      title: "Fashion Trends",
      description: "Stay updated with current fashion trends and styling tips",
    },
  ];

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: "#1A1A1A" }}>
        <Toolbar>
          <Box
            component="img"
            src="/tanhanger.png"
            alt="NO STYLIST Logo"
            sx={{
              width: isMobile ? 32 : 40,
              height: isMobile ? 32 : 40,
              mr: 2,
              objectFit: "contain",
            }}
          />
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            component="div"
            sx={{ flexGrow: 1, color: "#FFFCF7" }}
          >
            NO STYLIST
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              color="inherit"
              onClick={onLoginClick}
              sx={{
                border: "1px solid rgba(255, 252, 247, 0.3)",
                borderRadius: 1,
                px: 2,
                py: 1,
                "&:hover": {
                  backgroundColor: "rgba(255, 252, 247, 0.1)",
                  borderColor: "rgba(255, 252, 247, 0.5)",
                },
              }}
            >
              Log in
            </Button>
            <Button
              variant="contained"
              onClick={onDemoClick}
              sx={{
                backgroundColor: "#C49A88",
                color: "#1A1A1A",
                px: 2,
                py: 1,
                "&:hover": {
                  backgroundColor: "#B08570",
                },
              }}
            >
              Try Demo
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ px: isMobile ? 2 : 3 }}>
        {/* Hero Section */}
        <Box
          sx={{
            textAlign: "center",
            py: { xs: 4, md: 10 },
            px: { xs: 2, md: 4 },
            background: "linear-gradient(135deg, #F4EDE4 0%, #FFFCF7 100%)",
            borderRadius: 3,
            mt: { xs: 2, md: 3 },
            mb: { xs: 4, md: 6 },
          }}
        >
          <Typography
            variant={isMobile ? "h4" : "h2"}
            component="h1"
            sx={{
              fontWeight: 700,
              color: "#1A1A1A",
              mb: { xs: 2, md: 3 },
              letterSpacing: "-0.02em",
              px: { xs: 1, md: 0 },
            }}
          >
            Your Personal AI Stylist
          </Typography>
          <Typography
            variant={isMobile ? "body1" : "h5"}
            sx={{
              color: "#6B7B8C",
              mb: { xs: 3, md: 4 },
              maxWidth: 800,
              mx: "auto",
              lineHeight: 1.6,
              px: { xs: 1, md: 0 },
            }}
          >
            Transform your closet into a smart, organized wardrobe with
            AI-powered outfit recommendations and color coordination
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 2, sm: 2 }}
            justifyContent="center"
            sx={{ mt: { xs: 3, md: 4 }, px: { xs: 1, md: 0 } }}
          >
            <Button
              variant="contained"
              size={isMobile ? "medium" : "large"}
              onClick={onDemoClick}
              sx={{
                backgroundColor: "#1A1A1A",
                color: "#FFFCF7",
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 1.5, md: 2 },
                fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                fontWeight: 600,
                minWidth: { xs: "auto", sm: "auto" },
                "&:hover": {
                  backgroundColor: "#404040",
                },
              }}
            >
              Try Demo Free
            </Button>
            <Button
              variant="outlined"
              size={isMobile ? "medium" : "large"}
              onClick={onRegisterClick}
              sx={{
                borderColor: "#C49A88",
                color: "#1A1A1A",
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 1.5, md: 2 },
                fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                fontWeight: 600,
                minWidth: { xs: "auto", sm: "auto" },
                "&:hover": {
                  backgroundColor: "#F4EDE4",
                  borderColor: "#B08570",
                },
              }}
            >
              Create Account
            </Button>
          </Stack>
        </Box>

        {/* Features Section */}
        <Box sx={{ mb: { xs: 6, md: 8 } }}>
          <Typography
            variant={isMobile ? "h5" : "h3"}
            component="h2"
            sx={{
              textAlign: "center",
              fontWeight: 600,
              color: "#1A1A1A",
              mb: { xs: 4, md: 6 },
              letterSpacing: "-0.01em",
              px: { xs: 1, md: 0 },
            }}
          >
            Why Choose NO STYLIST?
          </Typography>
          <Grid container spacing={{ xs: 2, md: 4 }}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    backgroundColor: "#FFFCF7",
                    border: "1px solid #C49A88",
                    borderRadius: 3,
                    transition:
                      "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(26, 26, 26, 0.15)",
                    },
                  }}
                >
                  <CardContent
                    sx={{ p: { xs: 2, md: 3 }, textAlign: "center" }}
                  >
                    <Box sx={{ mb: { xs: 1.5, md: 2 } }}>{feature.icon}</Box>
                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      component="h3"
                      sx={{
                        fontWeight: 600,
                        color: "#1A1A1A",
                        mb: { xs: 1.5, md: 2 },
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant={isMobile ? "body2" : "body1"}
                      sx={{
                        color: "#6B7B8C",
                        lineHeight: 1.6,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* How It Works Section */}
        <Box
          sx={{
            backgroundColor: "#FFFCF7",
            borderRadius: 3,
            p: { xs: 3, md: 6 },
            border: "1px solid #C49A88",
            mb: { xs: 6, md: 8 },
          }}
        >
          <Typography
            variant={isMobile ? "h5" : "h3"}
            component="h2"
            sx={{
              textAlign: "center",
              fontWeight: 600,
              color: "#1A1A1A",
              mb: { xs: 4, md: 6 },
              letterSpacing: "-0.01em",
              px: { xs: 1, md: 0 },
            }}
          >
            How It Works
          </Typography>
          <Grid container spacing={{ xs: 3, md: 4 }}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    width: { xs: 60, md: 80 },
                    height: { xs: 60, md: 80 },
                    borderRadius: "50%",
                    backgroundColor: "#C49A88",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: { xs: 2, md: 3 },
                  }}
                >
                  <CloudUpload
                    sx={{ fontSize: { xs: 30, md: 40 }, color: "#FFFCF7" }}
                  />
                </Box>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  sx={{
                    fontWeight: 600,
                    color: "#1A1A1A",
                    mb: { xs: 1.5, md: 2 },
                  }}
                >
                  1. Upload Your Clothes
                </Typography>
                <Typography
                  variant={isMobile ? "body2" : "body1"}
                  sx={{ color: "#6B7B8C" }}
                >
                  Take photos of your clothing items and upload them to your
                  digital wardrobe
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    width: { xs: 60, md: 80 },
                    height: { xs: 60, md: 80 },
                    borderRadius: "50%",
                    backgroundColor: "#C49A88",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: { xs: 2, md: 3 },
                  }}
                >
                  <Palette
                    sx={{ fontSize: { xs: 30, md: 40 }, color: "#FFFCF7" }}
                  />
                </Box>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  sx={{
                    fontWeight: 600,
                    color: "#1A1A1A",
                    mb: { xs: 1.5, md: 2 },
                  }}
                >
                  2. AI Analysis
                </Typography>
                <Typography
                  variant={isMobile ? "body2" : "body1"}
                  sx={{ color: "#6B7B8C" }}
                >
                  Our AI analyzes colors, patterns, and styles to categorize
                  your items automatically
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "center" }}>
                <Box
                  sx={{
                    width: { xs: 60, md: 80 },
                    height: { xs: 60, md: 80 },
                    borderRadius: "50%",
                    backgroundColor: "#C49A88",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: { xs: 2, md: 3 },
                  }}
                >
                  <Build
                    sx={{ fontSize: { xs: 30, md: 40 }, color: "#FFFCF7" }}
                  />
                </Box>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  sx={{
                    fontWeight: 600,
                    color: "#1A1A1A",
                    mb: { xs: 1.5, md: 2 },
                  }}
                >
                  3. Create Outfits
                </Typography>
                <Typography
                  variant={isMobile ? "body2" : "body1"}
                  sx={{ color: "#6B7B8C" }}
                >
                  Build perfect outfits with intelligent color coordination and
                  style recommendations
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            textAlign: "center",
            py: { xs: 4, md: 8 },
            px: { xs: 2, md: 4 },
            background: "linear-gradient(135deg, #1A1A1A 0%, #404040 100%)",
            borderRadius: 3,
            color: "#FFFCF7",
          }}
        >
          <Typography
            variant={isMobile ? "h5" : "h3"}
            component="h2"
            sx={{
              fontWeight: 600,
              mb: { xs: 2, md: 3 },
              letterSpacing: "-0.01em",
            }}
          >
            Experience NO STYLIST Today
          </Typography>
          <Typography
            variant={isMobile ? "body1" : "h5"}
            sx={{
              mb: { xs: 3, md: 4 },
              opacity: 0.9,
              maxWidth: 600,
              mx: "auto",
            }}
          >
            Try our demo with sample wardrobe items and see how AI can transform
            your style
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            sx={{ px: { xs: 3, md: 0 } }}
          >
            <Button
              variant="contained"
              size={isMobile ? "medium" : "large"}
              onClick={onDemoClick}
              sx={{
                backgroundColor: "#C49A88",
                color: "#1A1A1A",
                px: { xs: 2, sm: 4, md: 6 },
                py: { xs: 1.5, md: 2 },
                fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                fontWeight: 600,
                minWidth: { xs: "auto", sm: "auto" },
                "&:hover": {
                  backgroundColor: "#B08570",
                },
              }}
            >
              Try Demo Now
            </Button>
            <Button
              variant="outlined"
              size={isMobile ? "medium" : "large"}
              onClick={onRegisterClick}
              sx={{
                borderColor: "#FFFCF7",
                color: "#FFFCF7",
                px: { xs: 2, sm: 4, md: 6 },
                py: { xs: 1.5, md: 2 },
                fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                fontWeight: 600,
                minWidth: { xs: "auto", sm: "auto" },
                "&:hover": {
                  backgroundColor: "rgba(255, 252, 247, 0.1)",
                  borderColor: "#FFFCF7",
                },
              }}
            >
              Create Account
            </Button>
          </Stack>
        </Box>

        {/* Feedback Card */}
        <Box
          sx={{
            mt: { xs: 4, md: 6 },
            backgroundColor: "#FFFCF7",
            borderRadius: 3,
            p: { xs: 3, md: 6 },
            border: "1px solid #C49A88",
            textAlign: "center",
          }}
        >
          <Typography
            variant={isMobile ? "h6" : "h4"}
            component="h2"
            sx={{
              fontWeight: 600,
              color: "#1A1A1A",
              mb: { xs: 1.5, md: 2 },
              letterSpacing: "-0.01em",
            }}
          >
            Help Us Improve
          </Typography>
          <Typography
            variant={isMobile ? "body2" : "h6"}
            sx={{
              color: "#6B7B8C",
              mb: { xs: 3, md: 4 },
              maxWidth: 500,
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            Your feedback helps us deliver better results
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              maxWidth: 600,
              mx: "auto",
              "& .MuiTextField-root": {
                mb: 3,
              },
            }}
          >
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={feedbackForm.name}
              onChange={handleInputChange}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#C49A88",
                  },
                  "&:hover fieldset": {
                    borderColor: "#B08570",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1A1A1A",
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={feedbackForm.email}
              onChange={handleInputChange}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#C49A88",
                  },
                  "&:hover fieldset": {
                    borderColor: "#B08570",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1A1A1A",
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Subject"
              name="subject"
              value={feedbackForm.subject}
              onChange={handleInputChange}
              required
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#C49A88",
                  },
                  "&:hover fieldset": {
                    borderColor: "#B08570",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1A1A1A",
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Message"
              name="message"
              value={feedbackForm.message}
              onChange={handleInputChange}
              required
              multiline
              rows={4}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#C49A88",
                  },
                  "&:hover fieldset": {
                    borderColor: "#B08570",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1A1A1A",
                  },
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              size={isMobile ? "medium" : "large"}
              startIcon={<Email />}
              disabled={isSubmitting}
              sx={{
                backgroundColor: "#1A1A1A",
                color: "#FFFCF7",
                px: { xs: 3, sm: 4, md: 6 },
                py: { xs: 1.5, md: 2 },
                fontSize: { xs: "0.9rem", sm: "1rem", md: "1rem" },
                fontWeight: 600,
                minWidth: { xs: "auto", sm: "auto" },
                "&:hover": {
                  backgroundColor: "#404040",
                },
                "&:disabled": {
                  backgroundColor: "#6B7B8C",
                  color: "#FFFCF7",
                },
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit Feedback"}
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={submitStatus.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={submitStatus.success ? "success" : "error"}
          sx={{
            width: "100%",
            backgroundColor: submitStatus.success ? "#E6F4EA" : "#FFE6E6",
            color: "#1A1A1A",
            border: `1px solid ${submitStatus.success ? "#AABBA5" : "#FF6B6B"}`,
          }}
        >
          {submitStatus.message}
        </Alert>
      </Snackbar>
    </>
  );
};
